/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { FeatureAppLoader } from '@feature-hub/react';
import styled from 'styled-components';
import { getVideoPlayerConfig } from '../../../helper/getVideoConfig';
import { UeElement } from '@oneaudi/falcon-tools';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import { useWindowSize } from '../../../helper/useWindowSize';

type Asset = {
  videoUrlLargeViewport: {
    assetValue: string;
  };
  videoUrlSmallViewport: {
    assetValue: string;
  };
  ueElementProperty: string;
  alternateDescription: string;
  videoHasAudio: boolean;
  preserveVideoWidth?: boolean;
  loop: boolean;

  closedCaption:
    | {
        path: string;
      }
    | undefined;
  autoPlay: boolean;
  alternateDescriptionPortrait: string;
  closedCaptionPortrait:
    | {
        path: string;
      }
    | undefined;
  audioDescription:
    | {
        path: string;
      }
    | undefined;
  audioDescriptionPortrait:
    | {
        path: string;
      }
    | undefined;
};

interface VideoProps {
  index: number;
  isCurrent: number;
  asset: Asset;
  ueElementProperty: string;
  height: number | undefined;
}

type Options = {
  root: null;
  rootElement: null;
  threshold: [0.01];
};

export const VideoPLayer: React.FC<VideoProps> = ({ index, isCurrent, asset, height }) => {
  const {
    videoUrlLargeViewport,
    videoUrlSmallViewport,
    ueElementProperty,
    alternateDescription: alt,
    alternateDescriptionPortrait: altPortrait,
    autoPlay,
    loop,
    closedCaption: caption,
    closedCaptionPortrait: captionPortrait,
    audioDescription,
    audioDescriptionPortrait,
    videoHasAudio: muted,
    preserveVideoWidth = false,
  } = asset;

  const videoPlayerWrapperRef = React.useRef<HTMLDivElement>(null);
  const serverSrcFile = process.env.IS_LEGACY ? '/fh/app.node.js' : '/fh/app-alpha.node.js';
  const clientSrcFile = process.env.IS_LEGACY ? '/fh/app.js' : '/fh/app-alpha.js';
  const videoPlayerFeatureAppBaseUrl = `https://fa-video-player.cdn.prod.collab.apps.one.audi/v5.5.4`;
  const videoPlayerFeatureAppSrc = `${videoPlayerFeatureAppBaseUrl}${clientSrcFile}`;
  const videoPlayerFeatureAppServerSrc = `${videoPlayerFeatureAppBaseUrl}${serverSrcFile}`;
  const [visible, setVisible] = React.useState(false);
  const { width } = useWindowSize();
  const isMobile = (width as number) < Number(SYS_BREAKPOINT_MD.replace('px', ''));
  const featureAppId = React.useId();

  const videoPlayerConfig = useMemo(() => {
    return getVideoPlayerConfig({
      alt,
      altPortrait,
      audioDescription: audioDescription?.path,
      audioDescriptionPortrait: audioDescriptionPortrait?.path,
      autoPlay,
      caption: caption?.path,
      captionPortrait: captionPortrait?.path,
      loop,
      muted,
      videoUrlLargeScreen: videoUrlLargeViewport.assetValue,
      videoUrlSmallScreen: videoUrlSmallViewport.assetValue,
      isMobile,
      preserveVideoWidth,
    });
  }, [videoUrlLargeViewport, videoUrlSmallViewport]);

  // React.useEffect(() => {
  //   if (visible && index === isCurrent) {
  //     setRenderVideo(true);
  //   }
  // }, [visible]);

  function isOnScreen(options: Options) {
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, options);

    if (videoPlayerWrapperRef.current) {
      observer.observe(videoPlayerWrapperRef.current);
    }
    // if (!visible) {
    //   setRenderVideo(false);
    // }

    return () => {
      if (videoPlayerWrapperRef.current) {
        observer.unobserve(videoPlayerWrapperRef.current);
      }
    };
  }

  React.useEffect(() => {
    if (!videoPlayerWrapperRef.current) return;
    isOnScreen({
      root: null,
      rootElement: null,
      threshold: [0.01],
    });

    return undefined;
  }, []);

  return (
    <UeElement type="media" property={ueElementProperty} label="Video">
      <VideoPlayerWrapper
        ref={videoPlayerWrapperRef}
        height={height}
        data-testid="video-player-wrapper"
      >
        {index === isCurrent && visible && typeof window !== 'undefined' ? (
          <FeatureAppLoader
            // featureAppId={videoPlayerId}
            config={videoPlayerConfig}
            featureAppId={`__FEATURE_APP_NAME__${featureAppId}`}
            serverSrc={videoPlayerFeatureAppServerSrc}
            src={videoPlayerFeatureAppSrc}
          />
        ) : null}
      </VideoPlayerWrapper>
    </UeElement>
  );
};

const VideoPlayerWrapper = styled.div<{ height: number | undefined }>`
  position: unset;
  height: ${(props) => props.height}px;
  padding: 0;
  div {
    height: ${(props) => props.height}px;
    padding-bottom: 0;
    /* position: unset; */
    video {
      height: ${(props) => props.height}px;
      position: unset;
    }
  }
`;
